/**
 * SIG Clone - Clients Index Route
 *
 * Route: /sig/clients
 * Layout: _auth (authenticated)
 *
 * Features:
 * - List all clients with grouping information
 * - Inline editing of client grouping (parent client)
 * - Search and filter
 * - Link to edit page
 *
 * @see packages/serverless/api/docs/monumenta-sig-clone-COMPLETE.md
 */
import { createFileRoute, Link } from '@tanstack/react-router'
import { useState, useMemo } from 'react'
import { useClients, useClientsDropdown, useUpdateClient } from '@/hooks/sig'
import { useSigUIStore } from '@/stores/sig-ui-store'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select'
import { Pencil, Search, Loader2, Check, X } from 'lucide-react'
import { toast } from 'sonner'
import type { ClientWithGrouping } from '@/types/sig-clone'

export const Route = createFileRoute('/_auth/sig/clients/')({
  component: SigClientsPage,
})

function SigClientsPage() {
  const { clients, loading, error, refetch } = useClients()
  const { clients: dropdownClients } = useClientsDropdown()
  const updateClient = useUpdateClient()

  // Local state
  const [searchTerm, setSearchTerm] = useState('')
  const [editingClientId, setEditingClientId] = useState<number | null>(null)
  const [editingValue, setEditingValue] = useState<number | null>(null)

  // Filter clients based on search
  const filteredClients = useMemo(() => {
    if (!searchTerm.trim()) return clients

    const term = searchTerm.toLowerCase()
    return clients.filter(
      (client) =>
        client.nome.toLowerCase().includes(term) ||
        client.nomeFantasia?.toLowerCase().includes(term) ||
        client.cgc?.includes(term) ||
        client.codigo?.toLowerCase().includes(term)
    )
  }, [clients, searchTerm])

  // Handle inline edit start
  const handleEditStart = (client: ClientWithGrouping) => {
    setEditingClientId(client.id)
    setEditingValue(client.clientePaiId ?? null)
  }

  // Handle inline edit cancel
  const handleEditCancel = () => {
    setEditingClientId(null)
    setEditingValue(null)
  }

  // Handle inline edit save
  const handleEditSave = async (clientId: number) => {
    try {
      await updateClient.mutateAsync({
        id: clientId,
        clientePaiId: editingValue,
      })
      toast.success('Agrupamento atualizado com sucesso!')
      setEditingClientId(null)
      setEditingValue(null)
    } catch (err) {
      toast.error('Erro ao atualizar agrupamento')
      console.error('Error updating client:', err)
    }
  }

  // Get parent client name for display
  const getParentClientName = (clientePaiId?: number) => {
    if (!clientePaiId) return '-'
    const parent = clients.find((c) => c.id === clientePaiId)
    return parent?.nomeFantasia || parent?.nome || '-'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <p className="text-destructive">Erro ao carregar clientes: {error}</p>
        <Button onClick={() => refetch()} variant="outline">
          Tentar novamente
        </Button>
      </div>
    )
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Clientes</h1>
          <p className="text-muted-foreground">
            Gerenciar agrupamento de clientes SIG
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome, CNPJ ou código..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
        <p className="text-sm text-muted-foreground">
          {filteredClients.length} de {clients.length} clientes
        </p>
      </div>

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[100px]">Ações</TableHead>
              <TableHead>Nome</TableHead>
              <TableHead>CGC</TableHead>
              <TableHead>Nome Fantasia</TableHead>
              <TableHead>Agrupamento</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredClients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8">
                  {searchTerm
                    ? 'Nenhum cliente encontrado para a busca.'
                    : 'Nenhum cliente cadastrado.'}
                </TableCell>
              </TableRow>
            ) : (
              filteredClients.map((client) => (
                <TableRow key={client.id}>
                  {/* Actions */}
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => window.location.href = `/sig/clients/${client.id}/edit`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </TableCell>

                  {/* Nome */}
                  <TableCell className="font-medium">{client.nome}</TableCell>

                  {/* CGC */}
                  <TableCell>{client.cgc || '-'}</TableCell>

                  {/* Nome Fantasia */}
                  <TableCell>{client.nomeFantasia || '-'}</TableCell>

                  {/* Agrupamento (inline editable) */}
                  <TableCell>
                    {editingClientId === client.id ? (
                      <div className="flex items-center gap-2">
                        <Select
                          value={editingValue?.toString() || 'none'}
                          onValueChange={(val) =>
                            setEditingValue(val === 'none' ? null : parseInt(val))
                          }
                        >
                          <SelectTrigger className="w-[200px]">
                            <SelectValue placeholder="Selecionar..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Sem agrupamento</SelectItem>
                            {dropdownClients
                              .filter((c) => c.id !== client.id)
                              .map((c) => (
                                <SelectItem key={c.id} value={c.id.toString()}>
                                  {c.nomeFantasia || c.nome}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditSave(client.id)}
                          disabled={updateClient.isPending}
                        >
                          {updateClient.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Check className="h-4 w-4 text-green-600" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleEditCancel}
                          disabled={updateClient.isPending}
                        >
                          <X className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    ) : (
                      <button
                        onClick={() => handleEditStart(client)}
                        className="text-left hover:underline cursor-pointer"
                      >
                        {getParentClientName(client.clientePaiId)}
                      </button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
